// Command palette — Ctrl+K. Lists every registered binding from the active keymap
// plus a router-driven set of "go to" navigation entries (Gateway, Masters, Vouchers, …).

import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useQuery } from '@tanstack/react-query';
import { useShortcutRegistry } from '@/shortcuts/registry';
import { useTallyShortcut } from '@/hooks/useTallyShortcut';
import { useAuthStore } from '@/store/auth';
import { KbdChip } from '@/components/primitives/KbdChip';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { navigateTo } from '@/lib/navigate';

const NAV_ENTRIES = [
  // Gateway + hubs
  { id: 'goto.gateway',   label: 'Gateway',           path: '/' },
  { id: 'goto.vouchers',  label: 'Vouchers',          path: '/vouchers' },
  { id: 'goto.reports',   label: 'Reports Hub',       path: '/reports' },
  { id: 'goto.admin',     label: 'Tenant Portal',     path: '/admin' },
  { id: 'goto.help',      label: 'Help · keyboard',   path: '/help' },

  // Voucher entry — quick-create
  { id: 'goto.new.sales',       label: 'New · Sales',        path: '/vouchers/new/sales' },
  { id: 'goto.new.purchase',    label: 'New · Purchase',     path: '/vouchers/new/purchase' },
  { id: 'goto.new.receipt',     label: 'New · Receipt',      path: '/vouchers/new/receipt' },
  { id: 'goto.new.payment',     label: 'New · Payment',      path: '/vouchers/new/payment' },
  { id: 'goto.new.contra',      label: 'New · Contra',       path: '/vouchers/new/contra' },
  { id: 'goto.new.journal',     label: 'New · Journal',      path: '/vouchers/new/journal' },
  { id: 'goto.new.credit-note', label: 'New · Credit Note',  path: '/vouchers/new/credit-note' },
  { id: 'goto.new.debit-note',  label: 'New · Debit Note',   path: '/vouchers/new/debit-note' },
  { id: 'goto.recurring',       label: 'Recurring vouchers', path: '/vouchers/recurring' },

  // Reports
  { id: 'goto.day-book',         label: 'Day Book',              path: '/reports/day-book' },
  { id: 'goto.trial',            label: 'Trial Balance',         path: '/reports/trial-balance' },
  { id: 'goto.pnl',              label: 'Profit & Loss',         path: '/reports/pnl' },
  { id: 'goto.bs',               label: 'Balance Sheet',         path: '/reports/balance-sheet' },
  { id: 'goto.cash-flow',        label: 'Cash Flow',             path: '/reports/cash-flow' },
  { id: 'goto.outstanding',      label: 'Bills Outstanding',     path: '/reports/outstanding' },
  { id: 'goto.party-statement',  label: 'Party Statement',       path: '/reports/party-statement' },
  { id: 'goto.group-summary',    label: 'Group Summary',         path: '/reports/group-summary' },
  { id: 'goto.msme',             label: 'MSME 43B(h) Payables',  path: '/reports/msme-payables' },
  { id: 'goto.stock-summary',    label: 'Stock Summary',         path: '/reports/stock-summary' },
  { id: 'goto.reorder',          label: 'Reorder Report',        path: '/reports/reorder' },
  { id: 'goto.gstr1',            label: 'GSTR-1',                path: '/reports/gstr-1' },
  { id: 'goto.gstr3b',           label: 'GSTR-3B',               path: '/reports/gstr-3b' },
  { id: 'goto.sales-register',   label: 'Sales Register',        path: '/reports/sales-register' },
  { id: 'goto.purchase-register',label: 'Purchase Register',     path: '/reports/purchase-register' },
  { id: 'goto.cn-register',      label: 'CN Register',           path: '/reports/credit-note-register' },
  { id: 'goto.dn-register',      label: 'DN Register',           path: '/reports/debit-note-register' },
  { id: 'goto.tds-liability',    label: 'TDS Liability',         path: '/reports/tds-liability' },
  { id: 'goto.catalogue',        label: 'Reports Catalogue · every report on your live books', path: '/reports-catalogue' },
  { id: 'goto.bi',               label: 'BI dashboards',         path: '/bi' },

  // Masters
  { id: 'goto.masters.parties',     label: 'Masters · Parties',           path: '/masters/parties' },
  { id: 'goto.masters.items',       label: 'Masters · Items',             path: '/masters/items' },
  { id: 'goto.masters.ledgers',     label: 'Masters · Ledgers',           path: '/masters/ledgers' },
  { id: 'goto.masters.voucher-types', label: 'Masters · Voucher Types',   path: '/masters/voucher-types' },
  { id: 'goto.masters.hsn',         label: 'Masters · HSN / SAC',         path: '/masters/hsn-sac' },
  { id: 'goto.masters.units',       label: 'Masters · Units',             path: '/masters/units' },
  { id: 'goto.masters.stock-groups',label: 'Masters · Stock Groups',      path: '/masters/stock-groups' },
  { id: 'goto.masters.tax-rates',   label: 'Masters · Tax Rates',         path: '/masters/tax-rates' },
  { id: 'goto.masters.cost-centres',label: 'Masters · Cost Centres',      path: '/masters/cost-centres' },
  { id: 'goto.masters.cost-categories', label: 'Masters · Cost Categories', path: '/masters/cost-categories' },

  // Operations + Admin
  { id: 'goto.banking',          label: 'Bank statement import', path: '/banking/import' },
  { id: 'goto.reconcile',        label: 'Bank reconciliation',   path: '/banking/reconcile' },
  { id: 'goto.tally-outbox',     label: 'Tally outbox',          path: '/admin/tally-outbox' },
  { id: 'goto.users',            label: 'Admin · Users',         path: '/admin/users' },
  { id: 'goto.branches',         label: 'Admin · Branches',      path: '/admin/branches' },
  { id: 'goto.audit-log',        label: 'Admin · Audit log',     path: '/admin/audit-log' },
  { id: 'goto.posting-periods',  label: 'Admin · Posting periods (close locks)', path: '/admin/posting-periods' },
  { id: 'goto.year-end',         label: 'Admin · Year-end close', path: '/admin/year-end' },
  { id: 'goto.ca-portal',        label: 'Accountant Portal',     path: '/admin/ca-portal' },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const keymap = useShortcutRegistry((s) => s.getKeymap());
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);

  // The full 300+ report catalogue — loaded once the palette first opens, so every
  // engine report is reachable from Ctrl+K. Only shown while the user is searching
  // (the default view stays compact).
  const catQ = useQuery<{ count: number; rows: Array<{ code: string; title: string; category: string }> }>({
    queryKey: ['report-engine-catalogue'],
    queryFn: async () => (await api.get('/reports-engine/catalogue')).data,
    enabled: open && !!activeCompanyId,
    staleTime: 10 * 60_000,
  });
  const catalogueRows = query.trim().length >= 2 ? (catQ.data?.rows ?? []) : [];

  // Canonical palette shortcut is Ctrl+K (matches Linear / Notion / Slack / VS Code).
  // We used to alias Alt+G and Ctrl+G to the same palette — it confused users badly.
  // The footer Alt G hint is now removed; Ctrl+K is the single way to open the palette.
  useTallyShortcut('nav.command-palette', () => setOpen((o) => !o));

  // Click on the Gateway search-pill (or any other surface) opens us.
  useEffect(() => {
    function onOpen() { setOpen(true); }
    window.addEventListener('orenza:open-palette', onOpen);
    return () => window.removeEventListener('orenza:open-palette', onOpen);
  }, []);

  // Esc closes — only registered WHILE THE PALETTE IS OPEN. Otherwise CommandPalette
  // would permanently capture Esc globally and steal it from FormDrawer / other dialogs.
  useTallyShortcut('window.escape', () => setOpen(false), open);

  // Reset query when closing
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  function goto(path: string) {
    setOpen(false);
    navigateTo(path);
  }

  function invokeBinding(id: string) {
    setOpen(false);
    // Defer to the next frame so the dialog can close before the action runs.
    requestAnimationFrame(() => {
      useShortcutRegistry.getState().invoke(id);
      // If no handler is registered for the current screen, invoke() returns false.
      // Phase 1 wires a toast system; until then, silent no-op is acceptable since
      // the binding is still listed in the palette for discoverability.
    });
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-obsidian/30 backdrop-blur-sm pt-[15vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-md border border-mist bg-snow shadow-hero"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Command palette" shouldFilter className="flex flex-col">
          <div className="flex items-center gap-3 border-b border-mist px-4 py-3">
            <span className="text-cool" aria-hidden>⌕</span>
            <Command.Input
              value={query}
              onValueChange={setQuery}
              autoFocus
              placeholder="Search screens, shortcuts and 300+ reports…"
              className="flex-1 bg-transparent text-body-md text-obsidian placeholder:text-cool outline-none"
            />
            <KbdChip>Esc</KbdChip>
          </div>

          <Command.List className="max-h-[420px] overflow-y-auto p-2">
            <Command.Empty className="px-3 py-6 text-center text-body-sm text-cool">
              No results.
            </Command.Empty>

            <Command.Group heading="Go to">
              {NAV_ENTRIES.map((n) => (
                <Command.Item
                  key={n.id}
                  value={`${n.label} ${n.id}`}
                  onSelect={() => goto(n.path)}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-sm px-3 py-2 text-body-sm text-obsidian cursor-pointer',
                    'aria-selected:bg-cobalt/[0.05] aria-selected:text-cobalt',
                  )}
                >
                  <span>{n.label}</span>
                  <span className="text-caption text-cool font-mono">{n.path}</span>
                </Command.Item>
              ))}
            </Command.Group>

            {catalogueRows.length > 0 && (
              <Command.Group heading={`All reports (${catQ.data?.count ?? ''})`} className="mt-2">
                {catalogueRows.map((r) => (
                  <Command.Item
                    key={r.code}
                    value={`${r.title} ${r.category} ${r.code}`}
                    onSelect={() => goto(`/r/${r.code}`)}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-sm px-3 py-2 text-body-sm text-obsidian cursor-pointer',
                      'aria-selected:bg-cobalt/[0.05] aria-selected:text-cobalt',
                    )}
                  >
                    <span>{r.title}</span>
                    <span className="text-caption text-cool">{r.category}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            <Command.Group heading="Shortcuts" className="mt-2">
              {keymap.filter((b) => b.showInPalette !== false).map((b) => (
                <Command.Item
                  key={b.id}
                  value={`${b.label} ${b.id} ${b.combos.join(' ')}`}
                  onSelect={() => invokeBinding(b.id)}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-sm px-3 py-2 text-body-sm text-obsidian cursor-pointer',
                    'aria-selected:bg-cobalt/[0.05] aria-selected:text-cobalt',
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-micro uppercase text-cool tracking-[0.12em]">{b.group}</span>
                    <span>{b.label}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    {b.combos.slice(0, 1).map((c) => (
                      <KbdChip key={c}>{prettyCombo(c)}</KbdChip>
                    ))}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>

          <div className="flex items-center justify-between border-t border-mist px-4 py-2 text-caption text-cool">
            <span className="flex items-center gap-2">
              <KbdChip>↑</KbdChip> <KbdChip>↓</KbdChip> Navigate
            </span>
            <span className="flex items-center gap-2">
              <KbdChip>↵</KbdChip> Select
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}

function prettyCombo(combo: string): string {
  return combo
    .split('+')
    .map((p) => {
      if (p === 'ctrl') return 'Ctrl';
      if (p === 'alt') return 'Alt';
      if (p === 'shift') return 'Shift';
      if (p === 'meta') return '⌘';
      if (p === 'escape') return 'Esc';
      if (/^f\d+$/.test(p)) return p.toUpperCase();
      return p.length === 1 ? p.toUpperCase() : p[0].toUpperCase() + p.slice(1);
    })
    .join(' ');
}
